import type { PointerEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

interface BottomSheetPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  count?: number;
  children: (requestClose: () => void) => ReactNode;
}

const CLOSE_DURATION_MS = 220;

const BottomSheetPanel = ({ isOpen, onClose, title, count, children }: BottomSheetPanelProps) => {
  const [closing, setClosing] = useState(false);
  const pointerStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) setClosing(false);
  }, [isOpen]);

  const requestClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose(), CLOSE_DURATION_MS);
  };

  const handleHeaderPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = e.clientY;
    const handlePointerUp = (ev: PointerEvent) => {
      const startY = pointerStartRef.current;
      pointerStartRef.current = null;
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      if (startY == null) return;
      if (ev.clientY - startY > 35) requestClose();
    };
    window.addEventListener('pointerup', handlePointerUp, { once: true });
    window.addEventListener('pointercancel', handlePointerUp, { once: true });
  };

  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    const prevPos = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevLeft = document.body.style.left;
    const prevRight = document.body.style.right;
    const prevWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = prevOverflow || '';
      document.body.style.position = prevPos || '';
      document.body.style.top = prevTop || '';
      document.body.style.left = prevLeft || '';
      document.body.style.right = prevRight || '';
      document.body.style.width = prevWidth || '';
      document.documentElement.style.overflow = prevHtmlOverflow || '';
      const y = Math.abs(parseInt(prevTop || '0', 10)) || 0;
      window.scrollTo(0, y);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-[430px] z-50 w-full pointer-events-auto overscroll-contain"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div
        className="bg-background border-t border-background/50 rounded-t-3xl shadow-2xl w-full flex flex-col overflow-hidden"
        style={{
          height: '70vh',
          animation: closing ? 'slideDown 0.22s ease-in forwards' : 'slideUp 0.3s ease-out',
        }}
      >
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes slideDown {
            from { transform: translateY(0); }
            to { transform: translateY(100%); }
          }
        `}</style>

        <div
          className="shrink-0 cursor-pointer"
          onClick={requestClose}
          onPointerDown={handleHeaderPointerDown}
          style={{ touchAction: 'none' }}
        >
          <div className="flex justify-center py-3">
            <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          </div>

          <div className="flex items-center justify-between px-6 pb-4">
            <div
              className="flex items-baseline w-full justify-center gap-2"
              style={{ paddingTop: 'var(--heading-top-offset, 0px)' }}
            >
              <h3 className="font-semibold text-md leading-none">{title}</h3>
              {typeof count === 'number' && count > 0 && (
                <span
                  className="text-sm leading-none text-muted-foreground"
                  style={{ fontFamily: 'var(--font-heading, var(--font-sans, ui-sans-serif, system-ui, sans-serif))' }}
                >
                  ({count})
                </span>
              )}
            </div>
          </div>
        </div>

        {children(requestClose)}
      </div>
    </div>
  );
};

export default BottomSheetPanel;
